import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../Components/Sidebar';
import { MdArrowDropDown } from "react-icons/md";
import excel from '../assests/images/excel img.png';
import { Link } from 'react-router-dom';
import Constant from '../Components/Utils/Constant';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactPaginate from 'react-paginate';
import * as XLSX from 'xlsx';

export default function Appcreate() {
    // States for data and UI control
    const [users, setUsers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [adminCreatedCount, setAdminCreatedCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [dropdownStates, setDropdownStates] = useState([]);

    // States for bulk actions
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showExpireConfirmation, setShowExpireConfirmation] = useState(false);

    // States for in-header filtering and sorting
    const [statusFilter, setStatusFilter] = useState(null); // For 'blocked'
    const [expiryFilter, setExpiryFilter] = useState(null); // For 'active'/'expired'
    const [appwatermark, setAppwatermark] = useState(null);
    const [sortBy, setSortBy] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');
    const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
    const [isWatermarkFilterOpen, setIsWatermarkFilterOpen] = useState(false);
    const [isKeyExpiryFilterOpen, setIsKeyExpiryFilterOpen] = useState(false);

    // States for individual actions
    const [userToDelete, setUserToDelete] = useState(null);
    const [userToBlock, setUserToBlock] = useState(null);
    const [showIndividualDeleteModal, setShowIndividualDeleteModal] = useState(false);
    const [showIndividualBlockModal, setShowIndividualBlockModal] = useState(false);
    
    // Refs
    const statusFilterRef = useRef(null);
    const watermarkFilterRef = useRef(null);
    const keyExpiryFilterRef = useRef(null);
    const tableRef = useRef(null);

    // Data Fetching
    const fetchUsers = async (page) => {
        try {
            const params = new URLSearchParams({ page });
            if (statusFilter) params.append('status', statusFilter); // For blocked
            if (expiryFilter) params.append('expiry', expiryFilter); // For active/expired
            if (appwatermark !== null) params.append('appwatermark', appwatermark);
            if (sortBy) {
                params.append('sortBy', sortBy);
                params.append('sortOrder', sortOrder);
            }

            const response = await axios.get(`${Constant.BASE_URL}/user/getUsersNotCreatedBy?${params.toString()}`);
            if (response.data && response.data.success) {
                setUsers(response.data.admins || []);
                setTotalCount(response.data.totalCount || 0);
                setAdminCreatedCount(response.data.adminCreatedCount || 0);
                setTotalPages(response.data.totalPages || 1);
                setCurrentPage(page);
            }
        } catch (error) {
            toast.error("Failed to fetch users.");
            console.error("Error fetching users:", error);
        }
    };

    // Effects
    useEffect(() => {
        fetchUsers(1);
        setSelectedUsers([]);
    }, [statusFilter, expiryFilter, appwatermark, sortBy, sortOrder]);

    useEffect(() => {
        setDropdownStates(Array(users.length).fill(false));
    }, [users]);
    
    useEffect(() => {
        function handleClickOutside(event) {
            if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
                setIsStatusFilterOpen(false);
            }
            if (watermarkFilterRef.current && !watermarkFilterRef.current.contains(event.target)) {
                setIsWatermarkFilterOpen(false);
            }
            if (keyExpiryFilterRef.current && !keyExpiryFilterRef.current.contains(event.target)) {
                setIsKeyExpiryFilterOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handlers
    const handlePageClick = ({ selected }) => {
        fetchUsers(selected + 1);
    };

    const handleSort = (column) => {
        const newSortOrder = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortBy(column);
        setSortOrder(newSortOrder);
    };

    const toggleDropdown = (index) => {
        setDropdownStates(prevStates => prevStates.map((state, i) => (i === index ? !state : false)));
    };
    
    const handleSelectAll = (e) => {
        setSelectedUsers(e.target.checked ? users.map(u => u._id) : []);
    };

    const handleCheckboxChange = (e, userId) => {
        setSelectedUsers(prev => e.target.checked ? [...prev, userId] : prev.filter(id => id !== userId));
    };

    const handleExportToExcel = () => {
        if (tableRef.current) {
            const ws = XLSX.utils.table_to_sheet(tableRef.current);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "AppCreatedUsers");
            XLSX.writeFile(wb, "AppCreatedUsers.xlsx");
        }
    };

    // Bulk Action Handlers
    const handleDeleteAll = async () => {
        setShowDeleteConfirmation(false);
        try {
            await axios.delete(`${Constant.BASE_URL}/user/deleteUsers`, {
                data: { userIds: selectedUsers }
            });
            toast.success("Users deleted successfully!");
            fetchUsers(currentPage);
            setSelectedUsers([]);
        } catch (error) {
            toast.error("Failed to delete users.");
        }
    };

    const handleExpireAll = async () => {
        setShowExpireConfirmation(false);
        try {
            await axios.put(`${Constant.BASE_URL}/user/users/expire`, { 
                userIds: selectedUsers 
            });
            toast.success("Users expired successfully!");
            fetchUsers(currentPage);
            setSelectedUsers([]);
        } catch (error) {
            toast.error("Failed to expire users.");
        }
    };

    // Individual Action Handlers
    const handleOpenIndividualDelete = (userId) => {
        setUserToDelete(userId);
        setShowIndividualDeleteModal(true);
        setDropdownStates([]);
    };

    const handleConfirmIndividualDelete = async () => {
        try {
            await axios.delete(`${Constant.BASE_URL}/user/delete/${userToDelete}`);
            toast.success("User deleted successfully!");
            fetchUsers(currentPage);
        } catch (error) {
            toast.error("Failed to delete user.");
        } finally {
            setShowIndividualDeleteModal(false);
            setUserToDelete(null);
        }
    };

    const handleOpenIndividualBlock = (user) => {
        setUserToBlock(user);
        setShowIndividualBlockModal(true);
        setDropdownStates([]);
    };

    const handleConfirmIndividualBlock = async () => {
        if (!userToBlock) return;
        try {
            const newBlockStatus = !userToBlock.block;
            await axios.put(`${Constant.BASE_URL}/user/updatebyId/${userToBlock._id}`, {
                block: newBlockStatus
            });
            toast.success(`User ${newBlockStatus ? 'blocked' : 'unblocked'} successfully!`);
            fetchUsers(currentPage);
        } catch (error) {
            toast.error("Failed to update user status.");
        } finally {
            setShowIndividualBlockModal(false);
            setUserToBlock(null);
        }
    };

    return (
        <Sidebar>
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="bg-grey-300 p-3 lg:p-3 xl:p-3 2xl:p-6">
                <div style={{ borderRadius: '0px 0px 11px 11px' }} className="bg-white h-[78.55vh] flex flex-col">
                    {/* Header Section */}
                    <div className="flex items-center bg-white px-4 py-3 lg:py-2 xl:py-3 2xl:py-3">
                        <div className="flex-1">
                                <div className="flex">
                                    <Link to="/users">
                                        <div style={{ boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.25)" }} className="lg:w-fit px-2 2xl:px-3 lg:py-1 xl:py-2 2xl:py-3 cursor-pointer flex justify-center items-center bg-white rounded-sm border ml-3">
                                            <div className='text-5lg lg:text-[10px] xl:text-[15px] 2xl:text-[28px] font-semibold'><p>Created Users: {adminCreatedCount}</p></div>
                                        </div>
                                    </Link>
                                    <Link to="/appcreate">
                                        <div style={{ background: 'radial-gradient(125% 125% at 85.42% 0%, #FFD6A6 0%, #FF9C28 100%)' }} className="lg:w-fit px-2 2xl:px-3 lg:py-1 xl:py-2 2xl:py-3 cursor-pointer flex justify-center items-center bg-white rounded-sm ml-3">
                                            <div className='text-5lg lg:text-[10px] xl:text-[15px] 2xl:text-[28px] font-semibold'><p>App Created Users: {totalCount}</p></div>
                                        </div>
                                    </Link>
                                </div>
                        </div>
                        <div className="flex-1 flex justify-end items-center">
                            <img src={excel} alt='excel' onClick={handleExportToExcel} className='cursor-pointer w-10 2xl:w-[80px] lg:w-[25px] xl:w-[35px] mx-5' />
                            <div
                                onClick={() => selectedUsers.length > 0 && setShowExpireConfirmation(true)}
                                style={{ background: 'radial-gradient(125% 125% at 85.42% 0%, #FFB761 0%, #FF8A00 100%)' }}
                                className={`px-2 2xl:px-3 py-2 cursor-pointer flex justify-center items-center rounded-md mr-2 ${selectedUsers.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <div className='text-4lg 2xl:text-4xl lg:text-[10px] xl:text-[15px]'><p>Expire All</p></div>
                            </div>
                            <div
                                onClick={() => selectedUsers.length > 0 && setShowDeleteConfirmation(true)}
                                style={{ background: 'radial-gradient(125% 125% at 85.42% 0%, #FFA6A6 0%, #F00 100%)' }}
                                className={`px-2 2xl:px-3 py-2 cursor-pointer flex justify-center items-center rounded-md ${selectedUsers.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <div className='text-4lg 2xl:text-4xl lg:text-[10px] xl:text-[15px]'><p>Delete All</p></div>
                            </div>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="flex-grow w-full overflow-hidden">
                        <div className="overflow-auto h-full">
                            <table ref={tableRef} className="w-full table-fixed table-hover table-bordered text-sm" id="myTable">
                                <thead className='bg-gray-100 sticky top-0 z-10'>
                                    <tr>
                                        <th className="p-2 text-center"><input type="checkbox" onChange={handleSelectAll} checked={users.length > 0 && selectedUsers.length === users.length} /></th>
                                        <th className="p-2 text-left">Action</th>
                                        <th className="p-2 text-left">Name</th>
                                        <th className="p-2 text-left">Phone</th>
                                        <th className="p-2 text-left">Email</th>
                                        <th className="p-2 text-left">Coupon Used</th>
                                        <th className="p-2 text-left">
                                            <div ref={statusFilterRef} className="relative">
                                                <button onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)} className="flex items-center font-bold w-full">Key Status <MdArrowDropDown /></button>
                                                {isStatusFilterOpen && (
                                                    <ul className="absolute z-20 w-32 bg-white border rounded shadow-lg text-left">
                                                        <li onClick={() => { setStatusFilter(null); setIsStatusFilterOpen(false); }} className="px-3 py-2 cursor-pointer hover:bg-gray-100">All</li>
                                                        <li onClick={() => { setStatusFilter('blocked'); setIsStatusFilterOpen(false); }} className="px-3 py-2 cursor-pointer hover:bg-gray-100">Blocked</li>
                                                    </ul>
                                                )}
                                            </div>
                                        </th>
                                        <th className="p-2 text-left">
                                            <div ref={watermarkFilterRef} className="relative">
                                                <button onClick={() => setIsWatermarkFilterOpen(!isWatermarkFilterOpen)} className="flex items-center font-bold w-full">App Watermark <MdArrowDropDown /></button>
                                                {isWatermarkFilterOpen && (
                                                    <ul className="absolute z-20 w-32 bg-white border rounded shadow-lg text-left">
                                                        <li onClick={() => { setAppwatermark(null); setIsWatermarkFilterOpen(false); }} className="px-3 py-2 cursor-pointer hover:bg-gray-100">All</li>
                                                        <li onClick={() => { setAppwatermark('true'); setIsWatermarkFilterOpen(false); }} className="px-3 py-2 cursor-pointer hover:bg-gray-100">OFF</li>
                                                        <li onClick={() => { setAppwatermark('false'); setIsWatermarkFilterOpen(false); }} className="px-3 py-2 cursor-pointer hover:bg-gray-100">ON</li>
                                                    </ul>
                                                )}
                                            </div>
                                        </th>
                                        <th className="p-2 text-left cursor-pointer" onClick={() => handleSort('createdby')}>Created By</th>
                                        <th className="p-2 text-left">
                                            <div ref={keyExpiryFilterRef} className="relative">
                                                <button onClick={() => setIsKeyExpiryFilterOpen(!isKeyExpiryFilterOpen)} className="flex items-center font-bold w-full">
                                                   <span className="cursor-pointer" onClick={(e) => { e.stopPropagation(); handleSort('subscription'); }}>
                                                        Key Expire On
                                                   </span>
                                                   <MdArrowDropDown />
                                                </button>
                                                {isKeyExpiryFilterOpen && (
                                                    <ul className="absolute z-20 w-32 bg-white border rounded shadow-lg text-left">
                                                        <li onClick={() => { setExpiryFilter(null); setIsKeyExpiryFilterOpen(false); }} className="px-3 py-2 cursor-pointer hover:bg-gray-100">All</li>
                                                        <li onClick={() => { setExpiryFilter('active'); setIsKeyExpiryFilterOpen(false); }} className="px-3 py-2 cursor-pointer hover:bg-gray-100">Active</li>
                                                        <li onClick={() => { setExpiryFilter('expired'); setIsKeyExpiryFilterOpen(false); }} className="px-3 py-2 cursor-pointer hover:bg-gray-100">Expired</li>
                                                    </ul>
                                                )}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {users.map((row, index) => (
                                        <tr key={row._id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}>
                                            <td className="p-2 text-center"><input type="checkbox" checked={selectedUsers.includes(row._id)} onChange={(e) => handleCheckboxChange(e, row._id)} /></td>
                                            <td className="p-2">
                                                <div className="relative inline-block">
                                                    <div style={{ boxShadow: '0px 4px 5px 0px rgba(0, 0, 0, 0.25)' }} className="w-[78px] py-1 bg-white rounded-md border" onClick={() => toggleDropdown(index)}>
                                                        <div className="flex items-center justify-center cursor-pointer">
                                                            <p className="text-[12px] px-2">Select</p> <MdArrowDropDown />
                                                        </div>
                                                    </div>
                                                    {dropdownStates[index] && (
                                                        <div style={{ boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.25)' }} className="absolute w-24 mt-2 bg-white border z-20 text-left">
                                                            <ul>
                                                                <li onClick={() => handleOpenIndividualDelete(row._id)} className="p-2 cursor-pointer hover:bg-gray-100 text-sm">Delete</li>
                                                                <li onClick={() => handleOpenIndividualBlock(row)} className="border-t p-2 cursor-pointer hover:bg-gray-100 text-sm">{row.block ? 'Unblock' : 'Block'}</li>
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-2 break-all">{row.name || 'N/A'}</td>
                                            <td className="p-2 break-all">{row.phone || 'N/A'}</td>
                                            <td className="p-2 break-all">{row.email || 'N/A'}</td>
                                            <td className="p-2 break-all">{row.cuponapplied || 'N/A'}</td>
                                            <td className={`p-2 font-semibold ${row.block ? 'text-gray-700 bg-gray-200' : (row.subscriptionexpiry ? 'text-red-500' : 'text-green-500')}`}>
                                              {row.block ? 'Blocked' : (row.subscriptionexpiry ? 'Expired' : 'Active')}
                                            </td>
                                            <td className="p-2 break-all">{row.watermark ? 'OFF' : 'ON'}</td>
                                            <td className="p-2 break-all">{row.createdby?.name || 'N/A'}</td>
                                            <td
                                                className={`p-2 break-all ${
                                                    row.block
                                                    ? "text-gray-700"
                                                    : row.subscriptionexpiry
                                                    ? "text-red-500"
                                                    : "text-green-500"
                                                }`}
                                                >
                                                {row.subscription
                                                    ? new Date(row.subscription).toLocaleDateString("en-GB", {
                                                        day: "2-digit",
                                                        month: "2-digit",
                                                        year: "numeric",
                                                      })
                                                    : "N/A"}
                                                </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    
                    {/* Pagination Section */}
                    <div className="flex items-center justify-between p-4 border-t bg-white">
                        <p className="text-sm text-gray-700">Showing <span className="font-medium">{(currentPage - 1) * 15 + 1}</span> to <span className="font-medium">{Math.min(currentPage * 15, totalCount)}</span> of <span className="font-medium">{totalCount}</span> results</p>
                        <ReactPaginate
                            previousLabel={"<"}
                            nextLabel={">"}
                            pageCount={totalPages}
                            forcePage={currentPage - 1}
                            onPageChange={handlePageClick}
                            containerClassName={"flex items-center space-x-1"}
                            activeClassName={"bg-blue-500 text-white rounded-md"}
                            pageClassName={"px-3 py-1 rounded-md hover:bg-gray-200"}
                            previousClassName={"px-3 py-1 rounded-md hover:bg-gray-200"}
                            nextClassName={"px-3 py-1 rounded-md hover:bg-gray-200"}
                        />
                    </div>
                </div>

                {/* Modals */}
                {showDeleteConfirmation && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white p-6 rounded-md shadow-lg w-full max-w-sm">
                            <h3 className="text-lg font-bold text-center">Confirm Deletion</h3>
                            <p className="text-center my-4">Are you sure you want to delete {selectedUsers.length} user(s)?</p>
                            <div className="flex justify-center gap-4 mt-6">
                                <button onClick={() => setShowDeleteConfirmation(false)} className="px-6 py-2 bg-gray-200 rounded-md">No</button>
                                <button onClick={handleDeleteAll} className="px-6 py-2 bg-red-600 text-white rounded-md">Yes</button>
                            </div>
                        </div>
                    </div>
                )}
                 {showExpireConfirmation && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white p-6 rounded-md shadow-lg w-full max-w-sm">
                            <h3 className="text-lg font-bold text-center">Confirm Expiration</h3>
                            <p className="text-center my-4">Are you sure you want to expire {selectedUsers.length} user(s)?</p>
                            <div className="flex justify-center gap-4 mt-6">
                                <button onClick={() => setShowExpireConfirmation(false)} className="px-6 py-2 bg-gray-200 rounded-md">No</button>
                                <button onClick={handleExpireAll} className="px-6 py-2 bg-orange-500 text-white rounded-md">Yes</button>
                            </div>
                        </div>
                    </div>
                )}
                {showIndividualDeleteModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white p-6 rounded-md shadow-lg w-full max-w-sm">
                            <h3 className="text-lg font-bold text-center">Confirm Deletion</h3>
                            <p className="text-center my-4">Are you sure you want to delete this user?</p>
                            <div className="flex justify-center gap-4 mt-6">
                                <button onClick={() => setShowIndividualDeleteModal(false)} className="px-6 py-2 bg-gray-200 rounded-md">No</button>
                                <button onClick={handleConfirmIndividualDelete} className="px-6 py-2 bg-red-600 text-white rounded-md">Yes</button>
                            </div>
                        </div>
                    </div>
                )}
                {showIndividualBlockModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white p-6 rounded-md shadow-lg w-full max-w-sm">
                            <h3 className="text-lg font-bold text-center">{userToBlock?.block ? "Confirm Unblock" : "Confirm Block"}</h3>
                            <p className="text-center my-4">Are you sure you want to {userToBlock?.block ? 'unblock' : 'block'} this user?</p>
                            <div className="flex justify-center gap-4 mt-6">
                                <button onClick={() => setShowIndividualBlockModal(false)} className="px-6 py-2 bg-gray-200 rounded-md">No</button>
                                <button onClick={handleConfirmIndividualBlock} className={`px-6 py-2 text-white rounded-md ${userToBlock?.block ? 'bg-green-600' : 'bg-red-600'}`}>Yes</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Sidebar>
    );
}